import React, { useState, useEffect, useMemo } from 'react';

export default function BdFiInspectionView({ setView, Header }) {
  const accent = '#006a4e';
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(null);
  const [tab, setTab] = useState('pending'); // 'pending' | 'approved' | 'rejected' | 'all'
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  // SBD Action state
  const [sbdFiDt, setSbdFiDt] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [nextInspectionDt, setNextInspectionDt] = useState('');
  const [rejectError, setRejectError] = useState('');

  const load = () => {
    window.fetch('/api/fi/orders', {
      headers: { Authorization: `Bearer ${localStorage.getItem('dj_token') || ''}` }
    })
      .then(res => res.json())
      .then(d => setRows(Array.isArray(d) ? d : []))
      .catch(() => setRows([]));
  };

  useEffect(() => { load(); }, []);

  const openModal = r => {
    setSel(r);
    setMsg('');
    setRejectError('');
    setRejectReason(r.reject_reason || '');
    setNextInspectionDt(r.next_inspection_dt || '');
    // Default approved FI date to factory proposed date or required FI date
    setSbdFiDt(r.factory_propose_dt || r.required_fi_dt || '');
  };

  const handleAction = action => {
    setRejectError('');
    if (action === 'REJECT') {
      if (!rejectReason.trim()) {
        setRejectError('Reason is COMPULSORY for rejecting inspection.');
        return;
      }
      if (!nextInspectionDt) {
        setRejectError('Next inspection date is COMPULSORY for rejecting inspection.');
        return;
      }
    } else if (action === 'APPROVE') {
      if (!sbdFiDt) {
        setRejectError('Approved FI Date is required.');
        return;
      }
    }

    setBusy(true);
    setMsg('');
    window.fetch(`/api/fi/${encodeURIComponent(sel.tas_no)}/sbd-action`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('dj_token') || ''}`
      },
      body: JSON.stringify({
        action,
        fi_date: sbdFiDt,
        reject_reason: rejectReason.trim(),
        next_inspection_dt: nextInspectionDt
      })
    })
      .then(res => res.json())
      .then(r => {
        setBusy(false);
        if (!r || r.error) {
          setMsg(r?.error || 'Action failed');
          return;
        }
        setSel(null);
        load();
      })
      .catch(err => {
        setBusy(false);
        setMsg(err.message || 'Action failed');
      });
  };

  const fmtDMY = d => {
    if (!d) return '—';
    const s = String(d).slice(0, 10);
    const p = s.split('-');
    if (p.length === 3) return `${p[2]}-${p[1]}-${p[0]}`;
    return s;
  };

  const titleCase = s => {
    if (!s) return '';
    return String(s).toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  };

  const counts = useMemo(() => {
    if (!rows) return { pending: 0, approved: 0, rejected: 0, all: 0 };
    return {
      pending: rows.filter(r => r.sbd_status === 'PENDING').length,
      approved: rows.filter(r => r.sbd_status === 'APPROVED').length,
      rejected: rows.filter(r => r.sbd_status === 'REJECTED').length,
      all: rows.length
    };
  }, [rows]);

  const list = useMemo(() => {
    if (!rows) return [];
    let filtered = rows;
    if (tab === 'pending') filtered = filtered.filter(r => r.sbd_status === 'PENDING');
    else if (tab === 'approved') filtered = filtered.filter(r => r.sbd_status === 'APPROVED');
    else if (tab === 'rejected') filtered = filtered.filter(r => r.sbd_status === 'REJECTED');

    if (q.trim()) {
      const s = q.trim().toLowerCase();
      filtered = filtered.filter(r =>
        `${r.tas_no} ${r.buyer || ''} ${r.factory || ''} ${r.merchandiser || ''}`.toLowerCase().includes(s)
      );
    }
    return filtered;
  }, [rows, tab, q]);

  const cell = { padding: '8px 10px', fontSize: 12, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' };
  const hd = { ...cell, color: 'var(--text3)', fontSize: 10, letterSpacing: 1, background: 'var(--bg3)', textAlign: 'left' };

  return (
    <div className="screen">
      <Header subtitle="SBD · FI INSPECTION WORKFLOW" />
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
        <button onClick={() => setView('bd')} style={{ background: 'none', border: 'none', color: accent, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <div>
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 20, color: accent }}>FI Inspection &amp; Approval Portal</div>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>SBD Team inspection review, date approvals, and rejection control</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 12, padding: '16px 20px 0', borderBottom: '1px solid var(--border)' }}>
        {[
          ['pending', 'AWAITING FI APPROVAL', counts.pending, '#b5651d'],
          ['approved', 'APPROVED', counts.approved, 'var(--green)'],
          ['rejected', 'REJECTED', counts.rejected, '#c62828'],
          ['all', 'ALL ORDERS', counts.all, 'var(--text2)']
        ].map(([tKey, label, cnt, color]) => (
          <div
            key={tKey}
            onClick={() => setTab(tKey)}
            style={{
              padding: '8px 14px',
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: tab === tKey ? 800 : 600,
              color: tab === tKey ? color : 'var(--text3)',
              borderBottom: tab === tKey ? `3px solid ${color}` : '3px solid transparent',
              transition: 'all 0.15s'
            }}
          >
            {label} ({cnt})
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Search TAS, Buyer, Factory, Merchandiser..."
          style={{ padding: '6px 12px', fontSize: 12, borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', width: 280 }}
        />
        <div style={{ fontSize: 12, color: 'var(--text3)', marginLeft: 'auto' }}>
          Showing <b>{list.length}</b> orders
        </div>
      </div>

      {/* Grid */}
      <div style={{ padding: '0 20px 24px', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              <th style={hd}>TAS NO</th>
              <th style={hd}>BUYER</th>
              <th style={hd}>FACTORY</th>
              <th style={hd}>ENQUIRY DT</th>
              <th style={hd}>DELIVERY DT</th>
              <th style={hd}>LEAD DAYS</th>
              <th style={hd}>REQ FI DATE</th>
              <th style={hd}>FACTORY PROPOSE</th>
              <th style={hd}>STATUS</th>
              <th style={hd}>FINAL FI DATE</th>
              <th style={hd}>ACTION BY / AT</th>
              <th style={{ ...hd, textAlign: 'center' }}>ACTION</th>
            </tr>
          </thead>
          <tbody>
            {!rows && (
              <tr><td colSpan="12" style={{ padding: 30, textAlign: 'center', color: 'var(--text3)' }}>Loading inspection records...</td></tr>
            )}
            {rows && list.length === 0 && (
              <tr><td colSpan="12" style={{ padding: 30, textAlign: 'center', color: 'var(--text3)' }}>No inspection orders found.</td></tr>
            )}
            {list.map(r => (
              <tr key={r.tas_no} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ ...cell, fontWeight: 700, color: accent }}>{r.tas_no}</td>
                <td style={cell}>{titleCase(r.buyer) || '—'}</td>
                <td style={cell}>{r.factory || '—'}</td>
                <td style={cell}>{fmtDMY(r.enquirydate)}</td>
                <td style={cell}>{fmtDMY(r.delivery_dt)}</td>
                <td style={{ ...cell, textAlign: 'center' }}>{r.lead_days || 120}</td>
                <td style={{ ...cell, fontWeight: 600 }}>{fmtDMY(r.required_fi_dt)}</td>
                <td style={{ ...cell, color: '#b5651d', fontWeight: 600 }}>
                  {fmtDMY(r.factory_propose_dt)}
                </td>
                <td style={cell}>
                  {r.sbd_status === 'APPROVED' && (
                    <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 800, background: '#e8f5e9', color: '#1b5e20' }}>APPROVED</span>
                  )}
                  {r.sbd_status === 'REJECTED' && (
                    <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 800, background: '#ffebee', color: '#c62828' }}>REJECTED</span>
                  )}
                  {r.sbd_status === 'PENDING' && (
                    <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 800, background: '#fff3e0', color: '#b5651d' }}>PENDING SBD</span>
                  )}
                </td>
                <td style={{ ...cell, fontWeight: 700, color: r.sbd_status === 'APPROVED' ? 'var(--green)' : 'var(--text)' }}>
                  {fmtDMY(r.sbd_final_fi_dt) || '—'}
                </td>
                <td style={cell}>
                  {r.sbd_action_by_name ? (
                    <div>
                      <div style={{ fontWeight: 600 }}>{r.sbd_action_by_name}</div>
                      <div style={{ fontSize: 10, color: 'var(--text3)' }}>{r.sbd_action_dt || '—'}</div>
                    </div>
                  ) : '—'}
                </td>
                <td style={{ ...cell, textAlign: 'center' }}>
                  <button
                    onClick={() => openModal(r)}
                    style={{
                      padding: '5px 12px',
                      borderRadius: 6,
                      border: 'none',
                      background: accent,
                      color: '#fff',
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >
                    Review / Action
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Review & Approve / Reject Modal */}
      {sel && (
        <div
          onClick={() => setSel(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--bg)', borderRadius: 12, border: '1px solid var(--border)',
              width: '100%', maxWidth: 580, padding: 24, boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: 12 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: accent }}>FI Inspection Review: {sel.tas_no}</div>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
                  {titleCase(sel.buyer)} · {sel.factory} · Merch: {titleCase(sel.merchandiser)}
                </div>
              </div>
              <button
                onClick={() => setSel(null)}
                style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text3)' }}
              >
                ✕
              </button>
            </div>

            {/* Date Summary Card */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, background: 'var(--bg3)', padding: 14, borderRadius: 8, margin: '16px 0' }}>
              <div>
                <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase' }}>Required FI Date</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginTop: 2 }}>{fmtDMY(sel.required_fi_dt)}</div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase' }}>Target FI (-20d)</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: accent, marginTop: 2 }}>{fmtDMY(sel.target_fi_dt)}</div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: '#b5651d', fontWeight: 700, textTransform: 'uppercase' }}>Factory Proposed Date</div>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#b5651d', marginTop: 2 }}>{fmtDMY(sel.factory_propose_dt)}</div>
              </div>
            </div>

            {rejectError && (
              <div style={{ background: '#ffebee', color: '#c62828', padding: '10px 14px', borderRadius: 6, fontSize: 12, fontWeight: 700, marginBottom: 12 }}>
                ⚠️ {rejectError}
              </div>
            )}
            {msg && (
              <div style={{ background: '#e8f5e9', color: '#1b5e20', padding: '10px 14px', borderRadius: 6, fontSize: 12, fontWeight: 700, marginBottom: 12 }}>
                {msg}
              </div>
            )}

            {/* SBD Approval Section */}
            <div style={{ border: '1px solid #c8e6c9', background: '#f1f8e9', padding: 14, borderRadius: 8, marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#2e7d32', marginBottom: 8, letterSpacing: 0.5 }}>
                ✓ APPROVE INSPECTION DATE (SBD)
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#1b5e20', display: 'block', marginBottom: 4 }}>
                    Approved FI Date:
                  </label>
                  <input
                    type="date"
                    value={sbdFiDt}
                    onChange={e => setSbdFiDt(e.target.value)}
                    style={{ width: '100%', padding: '6px 10px', fontSize: 13, borderRadius: 6, border: '1px solid #81c784' }}
                  />
                </div>
                <button
                  onClick={() => handleAction('APPROVE')}
                  disabled={busy}
                  style={{
                    padding: '8px 20px',
                    borderRadius: 6,
                    border: 'none',
                    background: 'var(--green)',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 12,
                    cursor: 'pointer',
                    marginTop: 18,
                    opacity: busy ? 0.6 : 1
                  }}
                >
                  {busy ? 'Saving...' : 'APPROVE'}
                </button>
              </div>
            </div>

            {/* SBD Rejection Section */}
            <div style={{ border: '1px solid #ffcdd2', background: '#ffebee', padding: 14, borderRadius: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#c62828', marginBottom: 8, letterSpacing: 0.5 }}>
                ✕ REJECT INSPECTION DATE (Reason &amp; Next Date COMPULSORY)
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#b71c1c', display: 'block', marginBottom: 4 }}>
                  Rejection Reason * (Compulsory):
                </label>
                <textarea
                  rows="2"
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  placeholder="Specify why the proposed inspection date is rejected..."
                  style={{ width: '100%', padding: '6px 10px', fontSize: 12, borderRadius: 6, border: '1px solid #e57373', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#b71c1c', display: 'block', marginBottom: 4 }}>
                    Next Inspection Date * (Compulsory):
                  </label>
                  <input
                    type="date"
                    value={nextInspectionDt}
                    onChange={e => setNextInspectionDt(e.target.value)}
                    style={{ width: '100%', padding: '6px 10px', fontSize: 13, borderRadius: 6, border: '1px solid #e57373' }}
                  />
                </div>
                <button
                  onClick={() => handleAction('REJECT')}
                  disabled={busy}
                  style={{
                    padding: '8px 20px',
                    borderRadius: 6,
                    border: 'none',
                    background: '#c62828',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 12,
                    cursor: 'pointer',
                    marginTop: 18,
                    opacity: busy ? 0.6 : 1
                  }}
                >
                  {busy ? 'Saving...' : 'REJECT'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
